import { openDB, type IDBPDatabase, type OpenDBCallbacks } from 'idb'
import { useAuthStore } from '@store/authStore'
import { getPortalContext } from '@utils/portalContext'

/**
 * The part of the offline outbox that must work before the lazy tenant bundle has loaded: the database layout, and the "hold" that is written
 * whenever a session ends. A hold means: entries made earlier on this device wait for the person's yes before anything is sent.
 * It lives in the same IndexedDB database as the entries (per tenant and user), in the `meta` store under the key `hold`.
 * This file is imported by the app entry, so a sign-out or an expired session records it even if the outbox module was never loaded.
 */
export const STORE = 'entries'
export const META = 'meta'
export const HOLD_EVENT = 'citybus-outbox-hold'
export const keyFor = (userId: string) => `citybus-outbox:${getPortalContext().tenantSlug ?? 'main'}:${userId}`

/** Opens (and creates, or completes an older layout of) the user's database. */
export async function openOutboxDb(key: string, cb: OpenDBCallbacks<unknown> = {}): Promise<IDBPDatabase> {
  let db = await openDB(key, undefined, {
    ...cb,
    upgrade(d) {
      if (!d.objectStoreNames.contains(STORE)) d.createObjectStore(STORE, { keyPath: 'op_id' })
      if (!d.objectStoreNames.contains(META)) d.createObjectStore(META)
    },
  })
  if (!db.objectStoreNames.contains(META)) { // a database made before the hold existed
    const next = db.version + 1
    db.close()
    db = await openDB(key, next, { ...cb, upgrade(d) { if (!d.objectStoreNames.contains(META)) d.createObjectStore(META) } })
  }
  return db
}

/** A rising number for ordering holds against adds, finer than a millisecond. */
export const stamp = () => (typeof performance !== 'undefined' ? performance.timeOrigin + performance.now() : Date.now())

// Fallback marker, written synchronously at sign-out, for when the IndexedDB write fails or the page leaves first. While it exists, replay is
// blocked until the confirm screen. The outbox copies it into IndexedDB later and removes it. Kept in sessionStorage, localStorage and memory.
const FB = 'citybus-outbox-holdfb:'
const fbMem = new Map<string, string>()
export const fallbackSet = (key: string, at = stamp()) => {
  fbMem.set(key, String(at))
  for (const st of ['sessionStorage', 'localStorage'] as const) try { window[st].setItem(FB + key, String(at)) } catch { /* the other copies remain */ }
}
const STORES = ['sessionStorage', 'localStorage'] as const
/** The stamps of every copy (memory, sessionStorage, localStorage). */
const copies = (key: string): { where: 'mem' | (typeof STORES)[number]; at: number }[] => {
  const out: { where: 'mem' | (typeof STORES)[number]; at: number }[] = []
  const m = fbMem.get(key); if (m !== undefined) out.push({ where: 'mem', at: Number(m) || 1 })
  for (const st of STORES) try { const v = window[st].getItem(FB + key); if (v !== null) out.push({ where: st, at: Number(v) || 1 }) } catch { /* ignore */ }
  return out
}
/** The newest fallback stamp, or null when there is none. */
export const fallbackGet = (key: string): number | null => { const c = copies(key); return c.length ? Math.max(...c.map((x) => x.at)) : null }
/** Removes the copies stamped at or before `at`. A newer copy (another tab signed out later) stays. */
export const fallbackClearUpTo = (key: string, at: number) => {
  for (const c of copies(key)) {
    if (c.at > at) continue
    if (c.where === 'mem') fbMem.delete(key); else try { window[c.where].removeItem(FB + key) } catch { /* ignore */ }
  }
}
/** Removes every copy. Only for tests and for a wipe of the whole device state. */
export const fallbackClear = (key: string) => fallbackClearUpTo(key, Infinity)

// Other tabs hear about holds set or cleared here (and the other way round), so a tab replaying does not rely on its own copy.
const CHANNEL = 'citybus-outbox'
let channel: BroadcastChannel | null = null
const chan = () => { try { return (channel ??= typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL) : null) } catch { return null } }
/** `at` is the version of the marker: a hold's own stamp, or the stamp of the release that ended it. A newer release beats an older hold everywhere. */
export const announceHold = (key: string, held: boolean, at: number) => { try { chan()?.postMessage({ key, held, at }) } catch { /* the persisted marker is still read before every request */ } }
export const onRemoteHold = (fn: (key: string, held: boolean, at: number) => void) => { try { chan()?.addEventListener('message', (e) => { const d = (e as MessageEvent).data; if (d && typeof d.key === 'string') fn(d.key, !!d.held, Number(d.at) || 0) }) } catch { /* no channel */ } }

// Hold writes in flight, per user+tenant database: one person's slow write never delays another's start-up.
/** Tells other tabs the queue changed (an entry added, sent, refused, discarded or confirmed), so they re-read it. Storage event when there is no BroadcastChannel. */
const QKEY = 'citybus-outbox-q'
export const announceQueue = () => {
  const c = chan()
  if (c) { try { c.postMessage({ queue: true }) } catch { /* the periodic re-read still happens */ } return }
  try { localStorage.setItem(QKEY, String(Math.random())) } catch { /* ignore */ }
}
export const onRemoteQueue = (fn: () => void) => {
  const c = chan()
  if (c) { try { c.addEventListener('message', (e) => { if ((e as MessageEvent).data?.queue) fn() }) } catch { /* no channel */ } return }
  if (typeof window !== 'undefined') window.addEventListener('storage', (e) => { if (e.key === QKEY) fn() })
}

const writes = new Map<string, Set<Promise<unknown>>>()
const WAIT_MS = 1500
/** Resolves when the hold writes in flight (for one database, or all) are done, or after 1.5 s. The fallback marker is the guard if it times out. */
export function holdSettled(key?: string): Promise<unknown> {
  const all = key ? [...(writes.get(key) ?? [])] : [...writes.values()].flatMap((w) => [...w])
  if (!all.length) return Promise.resolve()
  let timer: ReturnType<typeof setTimeout>
  return Promise.race([Promise.allSettled(all), new Promise((r) => { timer = setTimeout(r, WAIT_MS) })]).finally(() => clearTimeout(timer))
}

async function writeHold(key: string, at: number) {
  const db = await openOutboxDb(key, { blocking() { db.close() } })
  try { await db.put(META, { at }, 'hold') } finally { db.close() }
}

useAuthStore.subscribe((s, prev) => {
  if (!prev.user || s.user) return
  const key = keyFor(prev.user.id)
  const at = stamp()
  fallbackSet(key, at) // synchronous: it exists even if the page is gone before the write below finishes
  const p: Promise<unknown> = writeHold(key, at).then(() => fallbackClearUpTo(key, at), () => { /* storage unusable: the fallback marker stays */ }).finally(() => {
    writes.get(key)?.delete(p)
    announceHold(key, true, at)
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(HOLD_EVENT, { detail: key }))
  })
  if (!writes.has(key)) writes.set(key, new Set())
  writes.get(key)!.add(p)
})
